import { useState, useMemo, useRef, useEffect } from "react";
import CustomTable from "components/CustomTable";
import { useAccountLoginList } from "./useAccountLoginList";
import { FontAwesomeIcon, Icon } from "assets/icons";
import {
  LockOutlined,
  UnlockOutlined,
  DeleteOutlined,
  ReloadOutlined,
  WarningOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import { Col, Row, Input, Form, Select, Button, message, Radio, DatePicker, Popover } from "antd";
import CustomModal from "components/CustomModal";
import { useTranslation } from "react-i18next";
import {
  useCreateSellerUserAccount,
  useUpdateSellerUserAccount,
  useResetSellerUserPassword,
  useUpdateSellerUserStatus,
  useSellerPermissions,
} from "hooks/seller";
import { passwordGenerator, validator } from "utils/helperFuncs";
import { ACTIVITY_TABS } from "features/Agent/constants";
import moment from "moment";
import "./index.scss";

const { Option } = Select;

const copyToClipboard = (text) => {
  navigator.clipboard
    .writeText(text)
    .then(() => {
      message.success("Đã sao chép mật khẩu");
    })
    .catch(() => {
      message.error("Sao chép thất bại");
    });
};

export const AccountLoginList = ({ dataDetail, setActiveTab }) => {
  const [editForm] = Form.useForm();
  const [resetPasswordForm] = Form.useForm();
  const [lockReasonForm] = Form.useForm();

  const [editModalOpen, setEditModalOpen] = useState(false);

  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);

  const [lockModalOpen, setLockModalOpen] = useState(false);

  const [disableModalOpen, setDisableModalOpen] = useState(false);

  const [confirmCancelModalOpen, setConfirmCancelModalOpen] = useState(false);

  const [cancelingModal, setCancelingModal] = useState(null);

  const [selectedRecord, setSelectedRecord] = useState(null);

  const [searchText, setSearchText] = useState("");

  const [statusFilter, setStatusFilter] = useState([]);

  const [tempStatusFilter, setTempStatusFilter] = useState([]);

  const [filterDropdownVisible, setFilterDropdownVisible] = useState(false);

  const [selectedDates, setSelectedDates] = useState([null, null]);

  const [showDatePicker, setShowDatePicker] = useState(false);

  const [dateValue, setDateValue] = useState([null, null]);

  const [selectedRange, setSelectedRange] = useState([]);

  const overlayRef = useRef(null);

  // Get seller user permissions
  const {
    canCreateSellerUser,
    canUpdateSellerUser,
    canLockSellerUser,
    canDeleteSellerUser,
    loading: permLoading,
  } = useSellerPermissions();

  const handleEdit = (record) => {
    setSelectedRecord(record);
    editForm.setFieldsValue({
      fullName: record.fullname,
      email: record.email,
      phone: record.telephone,
      accountType: record.accountUserSellerType === "MANAGER" ? "manager" : "staff",
    });
    setEditModalOpen(true);
  };

  const handleResetPassword = (record) => {
    setSelectedRecord(record);
    resetPasswordForm.setFieldsValue({ email: record.email });
    setResetPasswordModalOpen(true);
  };

  const handleLock = (record) => {
    setSelectedRecord(record);
    setLockModalOpen(true);
  };

  const handleDisable = (record) => {
    setSelectedRecord(record);
    setDisableModalOpen(true);
  };

  const handleUnlock = (record) => {
    setSelectedRecord(record);
    setLockModalOpen(true);
  };
  useEffect(() => {
    if (!lockModalOpen) {
      lockReasonForm.resetFields();
    }
  }, [lockModalOpen]);

  useEffect(() => {
    if (!resetPasswordModalOpen) {
      resetPasswordForm.resetFields();
    }
  }, [resetPasswordModalOpen]);

  const { columns } = useAccountLoginList({
    onEdit: handleEdit,
    onResetPassword: handleResetPassword,
    onLock: handleLock,
    onUnlock: handleUnlock,
    onDisable: handleDisable,
    statusFilter,
    setStatusFilter,
    tempStatusFilter,
    setTempStatusFilter,
    filterDropdownVisible,
    setFilterDropdownVisible,
    canUpdate: canUpdateSellerUser,
    canResetPassword: canUpdateSellerUser,
    canLock: canLockSellerUser,
    canDelete: canDeleteSellerUser,
  });

  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [errorModalOpen, setErrorModalOpen] = useState(false);

  const [errorDetails, setErrorDetails] = useState(null);

  const [form] = Form.useForm();

  const { handleCreateSellerUserAccount, loading: createLoading } = useCreateSellerUserAccount();
  const { handleUpdateSellerUserAccount, loading: updateLoading } = useUpdateSellerUserAccount();
  const { handleResetSellerUserPassword, loading: resetLoading } = useResetSellerUserPassword();
  const { handleUpdateSellerUserStatus, loading: statusLoading } = useUpdateSellerUserStatus();

  const filteredUsers = useMemo(() => {
    let users = dataDetail?.users || [];
    if (searchText) {
      users = users.filter(
        (user) =>
          user.fullname?.toLowerCase().includes(searchText.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchText.toLowerCase())
      );
    }
    if (statusFilter.length > 0) {
      users = users.filter((user) => {
        const key =
          user?.status === "DISABLED" || user?.status === "DELETED"
            ? user?.status
            : user.needChangePassword
              ? "PENDING"
              : user?.status;
        return statusFilter.includes(key);
      });
    }
    if (selectedDates.length === 2 && selectedDates[0] && selectedDates[1]) {
      users = users.filter((user) => {
        if (!user.createdAt) return false;
        const userDate = moment(user.createdAt);
        return userDate.isBetween(selectedDates[0], selectedDates[1], "day", "[]");
      });
    }
    return users;
  }, [dataDetail?.users, searchText, statusFilter, selectedDates]);

  return (
    <>
      <div className="filter-potential-customer-container" style={{ padding: "12px 0" }}>
        <Row
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "16px",
          }}
        >
          <Col>
            <Popover
              open={showDatePicker}
              trigger="click"
              placement="bottomLeft"
              content={
                <div style={{ padding: 8 }}>
                  <DatePicker.RangePicker
                    format="DD/MM/YYYY"
                    allowClear
                    value={dateValue}
                    onChange={(dates) => {
                      if (!dates || !dates[0] || !dates[1]) {
                        setDateValue(null);
                        setSelectedDates([]);
                        return;
                      }
                      setDateValue(dates);
                      setSelectedDates(dates);
                      setShowDatePicker(false);
                    }}
                  />
                </div>
              }
            >
              <Button onClick={() => setShowDatePicker(true)}>
                <FontAwesomeIcon icon={Icon.faFilter} style={{ marginRight: 16 }} className="icon" />
                Ngày tạo
              </Button>
            </Popover>
          </Col>
          <Col flex="1">
            <Input
              className="custom-input"
              prefix={<FontAwesomeIcon icon={Icon.faSearch} />}
              placeholder="Tìm kiếm theo họ tên, email"
              allowClear
              onChange={(e) => setSearchText(e.target.value)}
              maxLength={255}
            />
          </Col>
          <Col flex="1"></Col>
          <Col>
            {canCreateSellerUser && (
              <Button type="primary" onClick={() => setIsModalOpen(true)}>
                {t("cart.add_new_user")}
              </Button>
            )}
          </Col>
        </Row>
      </div>

      <CustomTable columns={columns} dataSource={filteredUsers} isLeftFooter={false} pagination />
      {/* Thêm mới tài khoản */}
      <CustomModal
        title={
          <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "1.2rem" }}>{t("cart.add_new_user")}</div>
        }
        visible={isModalOpen}
        onOke={() => {
          form.validateFields().then(async (values) => {
            try {
              const response = await handleCreateSellerUserAccount({
                sellerId: dataDetail.id,
                fullName: values.fullName,
                email: values.email,
                password: values.password,
                accountUserSellerType: values.accountType === "manager" ? "MANAGER" : "STAFF",
              });
              if (response?.success) {
                message.success(t("account.createAccountSuccess"));
                setIsModalOpen(false);
                form.resetFields();
              } else if (response?.errors) {
                const error = response.errors[0];
                setErrorDetails({
                  message: error.message,
                  path: error.path,
                  details: error.extensions?.details || [],
                });
                setErrorModalOpen(true);
                setIsModalOpen(false);
              } else {
                message.error(response?.message || "Đã xảy ra lỗi");
              }
            } catch (error) {
              if (error.graphQLErrors && error.graphQLErrors.length > 0) {
                const graphQLError = error.graphQLErrors[0];
                setErrorDetails({
                  message: graphQLError.message,
                  path: graphQLError.path,
                  details: graphQLError.extensions?.details || [],
                });
                setErrorModalOpen(true);
                setIsModalOpen(false);
              } else {
                message.error(error.message || "Đã xảy ra lỗi");
              }
            }
          });
        }}
        selfVisibleControlled={false}
        footer={false}
        onCancel={() => {
          setConfirmCancelModalOpen(true);
          setCancelingModal("add");
        }}
        hideCancelButton={false}
        closable={false}
        centered={true}
        okeButtonLabel={t("common.create")}
        cancelButtonLabel={t("common.cancel")}
        bodyStyle={{ padding: 24 }}
        styleBtnGrp={{ display: "flex", justifyContent: "center", gap: 8 }}
      >
        {dataDetail?.ward?.version === "V1" && (
          <div
            style={{
              marginBottom: 16,
              marginTop: 10,
              padding: 12,
              backgroundColor: "#fff3cd",
              border: "1px solid #ffeaa7",
              borderRadius: 4,
              color: "#856404",
            }}
          >
            Sổ địa chỉ của đại lý có địa chỉ giao hàng chưa được cập nhật thông tin sau sáp nhập.{" "}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setActiveTab(ACTIVITY_TABS.BOOK_ADDRESS);
                setIsModalOpen(false);
                setTimeout(() => {
                  document.getElementById("activity-tabs")?.scrollIntoView({ behavior: "smooth" });
                }, 100);
              }}
            >
              Cập nhật ngay
            </a>
          </div>
        )}
        <Form form={form} layout="vertical" initialValues={{ accountType: "manager", password: passwordGenerator(8) }}>
          <Form.Item
            label={t("account.name")}
            name="fullName"
            rules={[{ required: true, message: t("Vui lòng nhập họ và tên") }]}
          >
            <Input placeholder={t("seller.placeholderInput.fullName")} maxLength={255} />
          </Form.Item>
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: t("Vui lòng nhập Email") },
              { type: "email", message: t("Không đúng định dạng email") },
            ]}
          >
            <Input placeholder={t("seller.placeholderInput.email")} />
          </Form.Item>

          <Form.Item
            label={t("account.password")}
            name="password"
            rules={[{ required: true, message: t("account.pleaseEnterPassword") }, validator({ type: "password" })]}
            validateTrigger={["onChange", "onBlur"]}
          >
            <Input.Password
              placeholder="Nhập mật khẩu đăng nhập"
              addonAfter={
                <span>
                  <CopyOutlined
                    style={{ cursor: "pointer", color: "#fff" }}
                    onClick={() => {
                      const passwordValue = form.getFieldValue("password");
                      if (passwordValue) {
                        copyToClipboard(passwordValue);
                      }
                    }}
                  />
                </span>
              }
            />
          </Form.Item>
          <Form.Item
            label={t("account.typeAccount")}
            name="accountType"
            rules={[{ required: true, message: t("account.pleaseSelectAccountType") }]}
          >
            <Select>
              <Option value="manager">{t("account.typeAccountManager")}</Option>
              <Option value="staff">{t("account.typeAccountStaff")}</Option>
            </Select>
          </Form.Item>
        </Form>
      </CustomModal>
      {/* Custom Error Modal */}
      <CustomModal
        title={
          <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "1.2rem" }}>
            <span style={{ color: "#ff4d4f" }}>Lỗi quyền truy cập</span>
          </div>
        }
        visible={errorModalOpen}
        onCancel={() => {
          setErrorModalOpen(false);
          setErrorDetails(null);
        }}
        onOke={() => {
          setErrorModalOpen(false);
          setErrorDetails(null);
        }}
        selfVisibleControlled={false}
        footer={false}
        hideCancelButton={true}
        closable={true}
        centered={true}
        okeButtonLabel={"Xác nhận"}
        bodyStyle={{ padding: 24 }}
        styleBtnGrp={{ display: "flex", justifyContent: "center", gap: 8 }}
        width={500}
      >
        <div style={{ textAlign: "center" }}>
          <WarningOutlined style={{ fontSize: 48, color: "#ff4d4f", marginBottom: 16 }} />

          {errorDetails?.details && errorDetails.details.length > 0 && (
            <div
              style={{
                backgroundColor: "#fff2f0",
                border: "1px solid #ffccc7",
                borderRadius: 4,
                padding: 12,
                textAlign: "left",
                maxHeight: 200,
                overflowY: "auto",
              }}
            >
              {errorDetails.details.map((detail, index) => (
                <div key={index} style={{ marginBottom: 8 }}>
                  {detail.reason && <div style={{ color: "#cf1322", fontWeight: 500 }}>{detail.reason}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </CustomModal>
      {/* Cập nhật thông tin tài khoản */}
      <CustomModal
        title={
          <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "1.2rem" }}>
            {t("account.updateAccountLogin")}
          </div>
        }
        visible={editModalOpen}
        onCancel={() => {
          setConfirmCancelModalOpen(true);
          setCancelingModal("update");
        }}
        hideConfirmButton={false}
        onOke={() => {
          editForm.validateFields().then(async (values) => {
            try {
              const response = await handleUpdateSellerUserAccount({
                userId: selectedRecord.id,
                fullName: values.fullName,
                email: values.email,
                accountUserSellerType: values.accountType === "manager" ? "MANAGER" : "STAFF",
              });
              if (response?.success) {
                message.success(response.message);
                setEditModalOpen(false);
                editForm.resetFields();
              } else if (response?.errors) {
                const error = response.errors[0];
                setErrorDetails({
                  message: error.message,
                  path: error.path,
                  details: error.extensions?.details || [],
                });
                setErrorModalOpen(true);
                setEditModalOpen(false);
              } else {
                message.error(response?.message || "Cập nhật tài khoản thất bại");
              }
            } catch (error) {
              if (error.graphQLErrors && error.graphQLErrors.length > 0) {
                const graphQLError = error.graphQLErrors[0];
                setErrorDetails({
                  message: graphQLError.message,
                  path: graphQLError.path,
                  details: graphQLError.extensions?.details || [],
                });
                setErrorModalOpen(true);
                setEditModalOpen(false);
              } else {
                message.error(error.message || "Cập nhật tài khoản thất bại");
              }
            }
          });
        }}
        selfVisibleControlled={false}
        okeButtonLabel={t("common.update")}
        cancelButtonLabel={t("common.cancel")}
        buttonLoading={updateLoading}
        hideCancelButton={false}
        closable={false}
        centered={true}
        footer={false}
        bodyStyle={{ padding: 24 }}
        styleBtnGrp={{ display: "flex", justifyContent: "center", gap: 8 }}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            label={t("account.name")}
            name="fullName"
            rules={[{ required: true, message: t("seller.validateMessage.fullName") }]}
          >
            <Input placeholder={t("seller.placeholderInput.fullName")} maxLength={255} />
          </Form.Item>
          <Form.Item
            label={"Email"}
            name="email"
            rules={[
              { required: true, message: t("validateMessage.email") },
              { type: "email", message: t("common.wrongFormatEmail") },
            ]}
          >
            <Input placeholder={t("seller.placeholderInput.email")} disabled />
          </Form.Item>
          <Form.Item
            label={t("account.typeAccount")}
            name="accountType"
            rules={[{ required: true, message: t("account.pleaseSelectAccountType") }]}
          >
            <Select>
              <Select.Option value="manager">{t("account.typeAccountManager")}</Select.Option>
              <Select.Option value="staff">{t("account.typeAccountStaff")}</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </CustomModal>
      {/* Cấp lại mật khẩu */}
      <CustomModal
        title={
          <div
            style={{
              textAlign: "center",
              fontWeight: "bold",
            }}
          >
            <div
              style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", marginBottom: 8 }}
            >
              <div
                style={{
                  background: "#E7F0FF",
                  borderRadius: "50%",
                  width: 60,
                  height: 60,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ReloadOutlined
                  style={{
                    color: "#2246DC",
                    fontSize: 40,
                  }}
                />
              </div>
            </div>
            <div style={{ textAlign: "center", fontWeight: "bold", marginBottom: 10, fontSize: "1.2rem" }}>
              {t("account.confirm_reset_password")}
            </div>
            <div style={{ textAlign: "center", fontWeight: "normal", fontSize: 14, marginBottom: 10 }}>
              {t("account.reset_password_description", { email: selectedRecord?.email })}
            </div>
            <Form form={resetPasswordForm} layout="vertical">
              <Form.Item
                label=""
                name="password"
                rules={[{ required: true, message: t("account.pleaseEnterPassword") }, validator({ type: "password" })]}
                validateTrigger={["onChange", "onBlur"]}
              >
                <Input.Password
                  placeholder="Nhập mật khẩu đăng nhập"
                  className="password-copy-fix"
                  addonAfter={
                    <span className="copy-addon">
                      <CopyOutlined
                        style={{ cursor: "pointer", color: "#fff" }}
                        onClick={() => {
                          const passwordValue = resetPasswordForm.getFieldValue("password");
                          if (passwordValue) {
                            copyToClipboard(passwordValue);
                          }
                        }}
                      />
                    </span>
                  }
                />
              </Form.Item>
            </Form>
          </div>
        }
        onOke={() => {
          resetPasswordForm.validateFields().then(async (values) => {
            try {
              const response = await handleResetSellerUserPassword({
                userId: selectedRecord.id,
                password: values.password,
              });
              if (response?.success) {
                message.success(response.message);
                setResetPasswordModalOpen(false);
                resetPasswordForm.resetFields();
              } else if (response?.errors) {
                const error = response.errors[0];
                setErrorDetails({
                  message: error.message,
                  path: error.path,
                  details: error.extensions?.details || [],
                });
                setErrorModalOpen(true);
                setResetPasswordModalOpen(false);
              } else {
                message.error(response?.message || "Cấp lại mật khẩu thất bại");
              }
            } catch (error) {
              if (error.graphQLErrors && error.graphQLErrors.length > 0) {
                const graphQLError = error.graphQLErrors[0];
                setErrorDetails({
                  message: graphQLError.message,
                  path: graphQLError.path,
                  details: graphQLError.extensions?.details || [],
                });
                setErrorModalOpen(true);
                setResetPasswordModalOpen(false);
              } else {
                message.error(error.message || "Cấp lại mật khẩu thất bại");
              }
            }
          });
        }}
        visible={resetPasswordModalOpen}
        selfVisibleControlled={false}
        hideOkeButton={false}
        hideCancelButton={false}
        onCancel={() => setResetPasswordModalOpen(false)}
        bodyStyle={{ padding: 24 }}
        closable={false}
        centered={true}
        footer={false}
      ></CustomModal>
      {/* Khoá tài khoản + Mở khoá tài khoản */}
      <CustomModal
        okeButtonLabel={selectedRecord?.status === "DISABLED" ? t("cart.unlock_account") : t("common.confirm")}
        onOke={async () => {
          if (selectedRecord?.status === "DISABLED") {
            try {
              await handleUpdateSellerUserStatus({
                id: selectedRecord.id,
                status: "ACTIVE",
              });
              message.success("Mở tài khoản thành công");
              setLockModalOpen(false);
            } catch (error) {
              if (error.graphQLErrors && error.graphQLErrors.length > 0) {
                const graphQLError = error.graphQLErrors[0];
                setErrorDetails({
                  message: graphQLError.message,
                  path: graphQLError.path,
                  details: graphQLError.extensions?.details || [],
                });
                setErrorModalOpen(true);
                setLockModalOpen(false);
              } else {
                message.error(error.message || "Mở tài khoản thất bại");
              }
            }
          } else {
            lockReasonForm.validateFields().then(async (values) => {
              const reasonMap = {
                stop_cooperation: "Đại lý ngừng hợp tác",
                violate_policy: "Đại lý vi phạm chính sách",
                other: values.otherReason,
              };
              try {
                await handleUpdateSellerUserStatus({
                  id: selectedRecord.id,
                  status: "DISABLED",
                  reason: "Khoá: " + selectedRecord.email + " - " + reasonMap[values.reason],
                });
                message.success(t("cart.lock_account_success"));
                setLockModalOpen(false);
                lockReasonForm.resetFields();
              } catch (error) {
                if (error.graphQLErrors && error.graphQLErrors.length > 0) {
                  const graphQLError = error.graphQLErrors[0];
                  setErrorDetails({
                    message: graphQLError.message,
                    path: graphQLError.path,
                    details: graphQLError.extensions?.details || [],
                  });
                  setErrorModalOpen(true);
                  setLockModalOpen(false);
                } else {
                  message.error(error.message || t("cart.lock_account_fail"));
                }
              }
            });
          }
        }}
        title={
          <>
            <div style={{ marginBottom: 8 }}>
              <div
                style={{
                  background: "#E7F0FF",
                  borderRadius: "50%",
                  width: 60,
                  height: 60,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {selectedRecord?.status === "DISABLED" ? (
                  <UnlockOutlined style={{ color: "#2246DC", fontSize: 40 }} />
                ) : (
                  <LockOutlined style={{ color: "#2246DC", fontSize: 40 }} />
                )}
              </div>
            </div>
            <div
              style={{
                textAlign: "center",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.2rem",
              }}
            >
              {selectedRecord?.status === "DISABLED" ? t("cart.unlock_account") : t("cart.confirm_lock_account")}
            </div>
            {selectedRecord?.status === "DISABLED" ? (
              <>
                <div style={{ fontSize: 14, textAlign: "center", fontWeight: "bold" }}>
                  {t("cart.confirm_unlock_account")}
                </div>
                <div style={{ fontSize: 14, textAlign: "center", fontWeight: "normal" }}>
                  {t("cart.account_will_be_active")}
                </div>
              </>
            ) : (
              <>
                <p style={{ fontSize: 14, textAlign: "center" }}>{t("cart.choose_reason_before_lock")}</p>
                <Form form={lockReasonForm} layout="vertical" initialValues={{ reason: "stop_cooperation" }}>
                  <Form.Item name="reason" rules={[{ required: true, message: t("cart.choose_reason_before_lock") }]}>
                    <Radio.Group style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      <Radio value="stop_cooperation" autoFocus>
                        {t("cart.reason_stop_cooperation")}
                      </Radio>
                      <Radio value="violate_policy">{t("cart.reason_violate_policy")}</Radio>
                      <Radio value="other">{t("common.other")}</Radio>
                    </Radio.Group>
                  </Form.Item>
                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) => prevValues.reason !== currentValues.reason}
                  >
                    {({ getFieldValue }) =>
                      getFieldValue("reason") === "other" ? (
                        <Form.Item
                          name="otherReason"
                          rules={[{ required: true, message: t("cart.enter_other_reason") }]}
                        >
                          <Input placeholder={t("Nhập lý do khác")} autoFocus />
                        </Form.Item>
                      ) : null
                    }
                  </Form.Item>
                </Form>
              </>
            )}
          </>
        }
        visible={lockModalOpen}
        selfVisibleControlled={false}
        hideConfirmButton={false}
        hideCancelButton={false}
        footer={false}
        onCancel={() => setLockModalOpen(false)}
        closable={false}
        centered={true}
        bodyStyle={{ padding: 24 }}
      ></CustomModal>
      {/* Vô hiệu hoá tài khoản */}
      <CustomModal
        title={
          <>
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  backgroundColor: "#E7F0FF",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <DeleteOutlined style={{ fontSize: 40, color: "#2246DC" }} />
              </div>
            </div>

            <h3 style={{ textAlign: "center", fontWeight: 600, marginBottom: 8 }}>
              {t("cart.confirm_disable_account")}
            </h3>

            <p style={{ textAlign: "center", color: "#666", marginBottom: 24 }}>{t("cart.account_will_be_disabled")}</p>
          </>
        }
        addMoreButton={
          <Button
            type="primary"
            danger
            loading={statusLoading}
            onClick={async () => {
              try {
                await handleUpdateSellerUserStatus({
                  id: selectedRecord.id,
                  status: "DELETED",
                  reason: "Vô hiệu hoá tài khoản " + selectedRecord.email,
                });
                message.success(t("cart.disable_account_success"));
                setDisableModalOpen(false);
              } catch (error) {
                if (error.graphQLErrors && error.graphQLErrors.length > 0) {
                  const graphQLError = error.graphQLErrors[0];
                  setErrorDetails({
                    message: graphQLError.message,
                    path: graphQLError.path,
                    details: graphQLError.extensions?.details || [],
                  });
                  setErrorModalOpen(true);
                  setDisableModalOpen(false);
                } else {
                  message.error(error.message || t("cart.disable_account_fail"));
                }
              }
            }}
          >
            {t("cart.disable_account")}
          </Button>
        }
        visible={disableModalOpen}
        selfVisibleControlled={false}
        hideConfirmButton={true}
        hideCancelButton={false}
        hideOkeButton={true}
        onCancel={() => setDisableModalOpen(false)}
        bodyStyle={{ padding: 24 }}
        closable={false}
        centered={true}
        footer={false}
      />
      {/* Xác nhận huỷ */}
      <CustomModal
        title={<div style={{ textAlign: "center", fontWeight: "bold", fontSize: "1.2rem" }}></div>}
        visible={confirmCancelModalOpen}
        onOke={() => {
          if (cancelingModal === "add") {
            setIsModalOpen(false);
            form.resetFields();
          } else if (cancelingModal === "update") {
            setEditModalOpen(false);
            editForm.resetFields();
          }
          setConfirmCancelModalOpen(false);
          setCancelingModal(null);
        }}
        onCancel={() => setConfirmCancelModalOpen(false)}
        selfVisibleControlled={false}
        footer={false}
        hideCancelButton={false}
        closable={false}
        centered={true}
        zIndex={1100}
        bodyStyle={{ padding: 16 }}
        width={350}
        styleBtnGrp={{ display: "flex", justifyContent: "center", gap: 8 }}
      >
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <WarningOutlined
            style={{
              color: "#f4c740ff",
              fontSize: 40,
            }}
          />
        </div>
        <div style={{ textAlign: "center" }}>
          Bạn chắc chắn huỷ bỏ thao tác với tài khoản đăng nhập? Nếu huỷ bỏ, thông tin bạn vừa thêm sẽ không được lưu
          lại.
        </div>
      </CustomModal>
    </>
  );
};
